import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence, useMotionValue } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
const slides = [{
  id: 1,
  image: 'https://cdn.magicpatterns.com/uploads/sTtRJm7yMtnZ8fERenRZ6C/0N8A8650.jpg',
  title: 'Fireside Evenings',
  subtitle: 'Where stories are shared',
  description: 'Massive stone fireplaces anchor every great room—the heart of mountain living.'
}, {
  id: 2,
  image: 'https://cdn.magicpatterns.com/uploads/5STms21tE5NEzBUAusiPhw/0N8A8729.jpg',
  title: 'The Mudroom',
  subtitle: 'Built for adventure',
  description: 'Purpose-built spaces for ski gear, hiking boots, and everything the mountains demand.'
}, {
  id: 3,
  image: 'https://cdn.magicpatterns.com/uploads/hLvP6BZbonMmKixMi9S1nY/98-Oct_25_2024_08_07pm-HYUu.jpg',
  title: 'Four-Season Living',
  subtitle: 'Covered outdoor spaces',
  description: 'Deep overhangs and radiant heat extend your living space through every season.'
}];
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0
  })
};
export function LifestyleSlider() {
  const [[currentIndex, direction], setCurrentIndex] = useState([0, 0]);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const dragX = useMotionValue(0);
  const paginate = useCallback((newDirection: number) => {
    const nextIndex = currentIndex + newDirection;
    if (nextIndex < 0) {
      setCurrentIndex([slides.length - 1, newDirection]);
    } else if (nextIndex >= slides.length) {
      setCurrentIndex([0, newDirection]);
    } else {
      setCurrentIndex([nextIndex, newDirection]);
    }
  }, [currentIndex]);
  const goToSlide = (index: number) => {
    const newDirection = index > currentIndex ? 1 : -1;
    setCurrentIndex([index, newDirection]);
  };
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => paginate(1), 6000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, paginate]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') paginate(-1);
      if (e.key === 'ArrowRight') paginate(1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [paginate]);
  const handleDragEnd = (_: any, info: {
    offset: {
      x: number;
    };
    velocity: {
      x: number;
    };
  }) => {
    const swipeThreshold = 50;
    const swipeVelocity = 500;
    if (info.offset.x > swipeThreshold || info.velocity.x > swipeVelocity) {
      paginate(-1);
    } else if (info.offset.x < -swipeThreshold || info.velocity.x < -swipeVelocity) {
      paginate(1);
    }
  };
  const currentSlide = slides[currentIndex];
  return <section className="relative h-screen bg-[#1A1A1A] overflow-hidden" onMouseEnter={() => setIsAutoPlaying(false)} onMouseLeave={() => setIsAutoPlaying(true)} data-id="element-161">
      <AnimatePresence initial={false} custom={direction} mode="popLayout" data-id="element-162">
        <motion.div key={currentIndex} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
        x: {
          type: 'spring',
          stiffness: 300,
          damping: 30
        },
        opacity: {
          duration: 0.4
        }
      }} drag="x" dragConstraints={{
        left: 0,
        right: 0
      }} dragElastic={0.2} onDragEnd={handleDragEnd} style={{
        x: dragX
      }} className="absolute inset-0 cursor-grab active:cursor-grabbing" data-id="element-163">
          <div className="absolute inset-0" data-id="element-164">
            <img src={currentSlide.image} alt={currentSlide.title} className="w-full h-full object-cover" data-id="element-165" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/90 via-[#1A1A1A]/50 to-transparent" data-id="element-166" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-transparent to-[#1A1A1A]/30" data-id="element-167" />
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="relative z-10 h-full flex flex-col justify-between px-6 md:px-16 lg:px-24 py-16 md:py-24" data-id="element-168">
        <div data-id="element-169">
          <motion.span initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-[#A07656] uppercase tracking-[0.3em] text-xs font-semibold" data-id="element-170">
            Lifestyle
          </motion.span>
        </div>

        <div className="max-w-3xl" data-id="element-171">
          <motion.h2 initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-[#F5F3F0] mb-8 leading-[1.1]" data-id="element-172">
            Designed for
            <br data-id="element-173" />
            <span className="italic font-semibold text-[#D4CFC8]/80" data-id="element-174">
              the Elements.
            </span>
          </motion.h2>

          <AnimatePresence mode="wait" data-id="element-175">
            <motion.div key={currentIndex} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.4
          }} className="space-y-4" data-id="element-176">
              <div className="flex items-center gap-4" data-id="element-177">
                <span className="text-5xl md:text-6xl font-serif font-bold text-[#A07656]/30" data-id="element-178">
                  0{currentSlide.id}
                </span>
                <div data-id="element-179">
                  <h3 className="text-xl md:text-2xl font-serif font-semibold text-[#F5F3F0]" data-id="element-180">
                    {currentSlide.title}
                  </h3>
                  <p className="text-[#A07656] text-sm uppercase tracking-widest font-medium" data-id="element-181">
                    {currentSlide.subtitle}
                  </p>
                </div>
              </div>
              <p className="text-[#D4CFC8]/60 text-lg max-w-md leading-relaxed" data-id="element-182">
                {currentSlide.description}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-8" data-id="element-183">
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="text-[#D4CFC8]/40 text-sm italic max-w-xs" data-id="element-184">
            "Homes built to embrace the mountain, not fight it."
          </motion.p>

          <div className="flex items-center gap-6" data-id="element-185">
            <div className="flex items-center gap-3" data-id="element-186">
              {slides.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className="group relative p-1" aria-label={`Go to slide ${index + 1}`} data-id="element-187">
                  <div className={`w-8 h-[2px] transition-all duration-300 ${index === currentIndex ? 'bg-[#A07656]' : 'bg-[#F5F3F0]/20 group-hover:bg-[#F5F3F0]/40'}`} data-id="element-188" />
                </button>)}
            </div>

            <div className="flex items-center gap-2" data-id="element-189">
              <button onClick={() => paginate(-1)} className="w-12 h-12 border-2 border-[#F5F3F0]/20 flex items-center justify-center text-[#F5F3F0]/60 hover:text-[#F5F3F0] hover:border-[#A07656] transition-all duration-300" aria-label="Previous slide" data-id="element-190">
                <ChevronLeft className="w-5 h-5" data-id="element-191" />
              </button>
              <button onClick={() => paginate(1)} className="w-12 h-12 border-2 border-[#F5F3F0]/20 flex items-center justify-center text-[#F5F3F0]/60 hover:text-[#F5F3F0] hover:border-[#A07656] transition-all duration-300" aria-label="Next slide" data-id="element-192">
                <ChevronRight className="w-5 h-5" data-id="element-193" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#F5F3F0]/10" data-id="element-194">
        <motion.div key={currentIndex} initial={{
        width: '0%'
      }} animate={{
        width: isAutoPlaying ? '100%' : '0%'
      }} transition={{
        duration: 6,
        ease: 'linear'
      }} className="h-full bg-[#A07656]/50" data-id="element-195" />
      </div>
    </section>;
}